import { action, reaction } from 'mobx';
import { requestFCMToken } from 'services/base/ipc';
import { IAccountsDomain } from 'services/accounts/types';
import { FCMService } from 'services/accounts/fcm_service';
import * as UserService from 'services/accounts/user_service';
import { BooleanStore } from 'ui/base/input';
import { GlobalThemeStore, Theme } from 'ui/base/theme';
import { GlobalSettingsStore } from 'services/base/app_settings';

export class SettingsStore {
  readonly domain: IAccountsDomain;
  readonly pushNotification: BooleanStore;
  readonly nightMode: BooleanStore;
  readonly hideOfflineDevices: BooleanStore;

  constructor({
    domain,
    pushNotification,
    nightMode,
    hideOfflineDevices,
  }: {
    domain: IAccountsDomain;
    pushNotification: boolean;
    nightMode: boolean;
    hideOfflineDevices: boolean;
  }) {
    this.domain = domain;
    this.pushNotification = new BooleanStore(pushNotification);
    this.nightMode = new BooleanStore(nightMode);
    this.hideOfflineDevices = new BooleanStore(hideOfflineDevices);

    // Note: There is a memory leak here due to these reactions never ever being disposed.

    // Update push notifications on change
    reaction(
      () => this.pushNotification.value,
      this.onPushNotificationUpdate,
    );

    // Update theme on change
    reaction(
      () => this.nightMode.value,
      () => {
        GlobalThemeStore.set(this.nightMode.value ? Theme.MIDNIGHT : Theme.DAYLIGHT);
      },
    );

    // Update hide offline devices on change
    reaction(
      () => this.hideOfflineDevices.value,
      () => {
        GlobalSettingsStore.set({
          ...GlobalSettingsStore.get(),
          hideOfflineDevices: this.hideOfflineDevices.value,
        });
      },
    );
  }

  @action.bound
  async onPushNotificationUpdate() {
    UserService.patch({
      domain: this.domain,
      pushNotifications: this.pushNotification.value,
    });
    if (this.pushNotification.value) {
      try {
        const fcmToken = await requestFCMToken();
        FCMService.update(fcmToken);
      } catch (e) {
        // Do nothing if the user doesn't allow push notifications
      }
    }
  }
}
